import React from "react";
import LegalLayout from "./LegalLayout";
import useSeo from "../lib/useSeo";
import { buildCanonical } from "../lib/insightsLinks";

export default function LegalPrivacy() {
  useSeo({
    title: "Privacy Policy — Linked2Marketing",
    description:
      "How Linked2Marketing collects, uses, stores, and protects personal information under the UK GDPR and the Data Protection Act 2018.",
    canonical: buildCanonical("/privacy"),
    ogType: "website",
  });

  return (
    <LegalLayout
      eyebrow="Legal · UK GDPR"
      title="Privacy Policy"
      intro="This policy explains how Linked2Marketing collects, uses, stores, and protects personal information. It is written to comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018."
      lastUpdated="28 February 2026"
      testid="page-privacy"
    >
      <h2>1. Who we are</h2>
      <p>
        Linked2Marketing (“we”, “us”, “our”) is a digital systems studio based in the
        United Kingdom. We are the data controller responsible for any personal
        information you submit through this website. You can contact us at{" "}
        <a href="mailto:contact@linked2marketing.com">contact@linked2marketing.com</a>.
      </p>

      <h2>2. What information we collect</h2>
      <p>
        We only collect information that you choose to send us, or that is
        automatically generated when you interact with this site. This includes:
      </p>
      <ul>
        <li>
          <strong>Enquiry information</strong> — your name, email address,
          business or brand name, website (optional), sector, priority, current
          tools, and message — when you complete the enquiry form.
        </li>
        <li>
          <strong>Technical information</strong> — anonymised analytics such as
          pages viewed, approximate location, device type, and referral source,
          collected via Google Analytics 4 only after you give consent.
        </li>
      </ul>

      <h2>3. Lawful basis for processing</h2>
      <p>We process personal information under the following lawful bases:</p>
      <ul>
        <li>
          <strong>Legitimate interests</strong> — to respond to your enquiry
          and to operate, secure, and improve this website.
        </li>
        <li>
          <strong>Consent</strong> — for non-essential cookies and analytics,
          which we only set after you click “Accept” in our cookie banner.
        </li>
        <li>
          <strong>Legal obligation</strong> — where we are required to retain
          records (for example, basic accounting information for HMRC).
        </li>
      </ul>

      <h2>4. How we use your information</h2>
      <p>We use the information you give us to:</p>
      <ul>
        <li>respond personally to your enquiry, usually within one business day;</li>
        <li>provide further information about our work, only if you have requested it;</li>
        <li>understand how the site is used in aggregate (only with consent);</li>
        <li>protect the site against spam and abuse.</li>
      </ul>
      <p>
        We do not sell your information. We do not share it with third parties
        for marketing purposes. We do not use it to build advertising audiences.
      </p>

      <h2>5. Who has access to your information</h2>
      <p>
        Enquiries are stored in a secure database operated by our hosting
        provider and are accessible only to Linked2Marketing personnel. Email
        notifications are delivered via Gmail (Google Workspace) acting as a
        data processor. Analytics, where consented, are processed by Google in
        an anonymised, aggregated form.
      </p>

      <h2>6. How long we keep your information</h2>
      <p>
        Enquiry data is kept for as long as is necessary to handle and follow up
        on your enquiry, and for a reasonable period afterwards in case you get
        in touch again. If you ask us to delete your information, we will do so
        promptly unless we are required to keep it by law.
      </p>

      <h2>7. International transfers</h2>
      <p>
        Some of the services we rely on (for example Google Workspace and
        Google Analytics) may transfer data outside the United Kingdom. Where
        this happens, the transfers are protected by appropriate safeguards
        such as the UK International Data Transfer Agreement or equivalent
        contractual mechanisms.
      </p>

      <h2>8. Your rights under UK GDPR</h2>
      <p>You have the right to:</p>
      <ul>
        <li>access the personal information we hold about you;</li>
        <li>request that we correct any inaccurate information;</li>
        <li>request that we delete your information;</li>
        <li>object to or restrict processing;</li>
        <li>withdraw consent for analytics at any time, by clearing your cookies or contacting us;</li>
        <li>request a copy of your data in a portable format;</li>
        <li>
          lodge a complaint with the Information Commissioner’s Office (ICO)
          at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
        </li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:contact@linked2marketing.com">contact@linked2marketing.com</a>.
      </p>

      <h2>9. Security</h2>
      <p>
        We use industry-standard measures to protect personal information,
        including HTTPS encryption, access-controlled admin systems, and
        server-side credential storage. No system is perfectly secure, and we
        encourage you to send sensitive information only by encrypted channels.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        announced on this page. The “Last updated” date above reflects the most
        recent revision.
      </p>
    </LegalLayout>
  );
}
